// The home calendar (Visual Direction, Calendar / Day): a month of nights,
// weeks starting on Monday, each night in one state. The marks come from
// GET /api/calendar; everything else follows from the dates.
import { api, ApiError } from '../api/client';
import type { CalendarResponse, NightMark } from '../api/types';
import { createStore } from '../store';

/**
 * selected: the night home shows; tonight: ringed when another night is shown;
 * all / some: the marks; past: an untouched night; closed: before the first
 * night or still to come, not tappable.
 */
export type DayState = 'selected' | 'tonight' | 'all' | 'some' | 'past' | 'closed';

export const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function parts(month: string): [number, number] {
  return month.split('-').map(Number) as [number, number];
}

export function addMonths(month: string, months: number): string {
  const [y, m] = parts(month);
  return new Date(Date.UTC(y, m - 1 + months, 1)).toISOString().slice(0, 7);
}

/** "October 2026". */
export function formatMonth(month: string): string {
  const [y, m] = parts(month);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

/** The month's weeks, Monday first, with null for the days of the months around it. */
export function monthWeeks(month: string): Array<Array<string | null>> {
  const [y, m] = parts(month);
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lead = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7;
  const cells: Array<string | null> = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= days; d++) cells.push(`${month}-${String(d).padStart(2, '0')}`);
  while (cells.length % 7) cells.push(null);
  const weeks: Array<Array<string | null>> = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export interface DayContext {
  /** The night home shows. */
  shown: string;
  tonight: string;
  firstNight: string;
  marks: ReadonlyMap<string, NightMark>;
}

export function dayState(date: string, ctx: DayContext): DayState {
  if (date < ctx.firstNight || date > ctx.tonight) return 'closed';
  if (date === ctx.shown) return 'selected';
  if (date === ctx.tonight) return 'tonight';
  const mark = ctx.marks.get(date);
  return mark === 'all' ? 'all' : mark === 'some' ? 'some' : 'past';
}

export interface CalendarState {
  month: string | null;
  data: CalendarResponse | null;
  error: string | null;
}

export const calendarStore = createStore<CalendarState>({ month: null, data: null, error: null });

let loadToken = 0;

/** Load a month's marks. The marks change as plays finish, so every opening asks again. */
export async function loadCalendar(month: string): Promise<void> {
  const token = ++loadToken;
  const current = calendarStore.get();
  calendarStore.set({ month, data: current.month === month ? current.data : null, error: null });
  try {
    const data = await api.calendar(month);
    if (token !== loadToken) return;
    calendarStore.set({ month, data, error: null });
  } catch (err) {
    if (token !== loadToken) return;
    calendarStore.set({ ...calendarStore.get(), error: err instanceof ApiError ? err.message : 'The calendar is out of reach.' });
  }
}
